// Shared per-member return metrics: XIRR (money-weighted) and CAGR.
// Extracted so both MyHoldings and the Members table can use them.

/**
 * XIRR via Newton's method.
 * cashflows: [{ date: Date|string, amount: number }]
 *   outflows (you put money in) MUST be negative
 *   inflows  (you took money out / current value) MUST be positive
 */
export function xirr(cashflows, guess = 0.1) {
  if (!cashflows || cashflows.length < 2) return null;

  const sorted = [...cashflows]
    .map((c) => ({ date: c.date instanceof Date ? c.date : new Date(c.date), amount: c.amount }))
    .sort((a, b) => a.date - b.date);

  const t0 = sorted[0].date;
  const years = sorted.map((c) => (c.date - t0) / (365.25 * 24 * 3600 * 1000));
  const amounts = sorted.map((c) => c.amount);

  const hasPos = amounts.some((a) => a > 0);
  const hasNeg = amounts.some((a) => a < 0);
  if (!hasPos || !hasNeg) return null;

  let rate = guess;
  for (let i = 0; i < 100; i++) {
    let npv = 0;
    let dnpv = 0;
    for (let k = 0; k < amounts.length; k++) {
      const denom = Math.pow(1 + rate, years[k]);
      npv += amounts[k] / denom;
      if (years[k] !== 0) dnpv -= (years[k] * amounts[k]) / (denom * (1 + rate));
    }
    if (Math.abs(npv) < 1e-6) return rate;
    if (dnpv === 0) return null;
    const next = rate - npv / dnpv;
    if (!isFinite(next)) return null;
    if (Math.abs(next - rate) < 1e-7) return next;
    rate = next;
    if (rate < -0.9999) rate = -0.9999; // prevent exploding
  }
  return rate;
}

/** Compound annual growth rate. Returns rate as a decimal (e.g. 0.12 = 12%). */
export function cagr(startValue, endValue, years) {
  if (!startValue || startValue <= 0 || !years || years <= 0) return null;
  return Math.pow(endValue / startValue, 1 / years) - 1;
}

/**
 * Returns { xirr, cagr, years, firstDate } for a single member.
 *   xirr / cagr are in PERCENTS (e.g. 12.34), or null if not computable.
 *   years is a number (e.g. 1.19), firstDate is the ISO date of first txn.
 *
 * Cashflows model:
 *   - Subscription (cash in)  → negative outflow at that date
 *   - Redemption  (cash out)  → positive inflow at that date
 *   - Bonus                   → ignored (no real cash movement)
 *   - Final point             → today's overall current value (positive)
 */
export function computeMemberMetrics(member, transactions, now = new Date()) {
  if (!member) return { xirr: null, cagr: null, years: null, firstDate: null };

  const txs = (transactions || []).filter((c) => c.memberId === member.id);
  if (txs.length === 0) return { xirr: null, cagr: null, years: null, firstDate: null };

  const asc = [...txs].sort((a, b) => new Date(a.date) - new Date(b.date));
  const firstDate = new Date(asc[0].date);
  const years = (now - firstDate) / (365.25 * 24 * 3600 * 1000);

  const flows = asc
    .filter((t) => t.type !== "bonus")
    .map((t) => {
      const cr = t.fundsCr || 0;
      const dr = t.fundsDr || 0;
      return { date: new Date(t.date), amount: cr > 0 ? -cr : dr };
    });

  const finalValue =
    member.overallCurrentValue != null ? member.overallCurrentValue : member.currentValue || 0;

  flows.push({ date: now, amount: finalValue });

  const xirrRate = xirr(flows);
  const cagrRate = cagr(member.capitalInvested || 0, finalValue, years);

  return {
    xirr: xirrRate != null ? xirrRate * 100 : null,
    cagr: cagrRate != null ? cagrRate * 100 : null,
    years: years > 0 ? +years.toFixed(2) : 0,
    firstDate: asc[0].date,
  };
}
